<!-- 
Dialog for manually removing a provider from a levy collection.

This is done by creating a "RemoveProvider" override.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog lg">
    <v-card>
      <v-toolbar color="secondary" flat dark>
        <v-toolbar-title>{{ title }}</v-toolbar-title>
      </v-toolbar>
      <our-busy-indicator :show="saving"/>
      <our-loading-indicator v-if="loading"/>
      <v-card-text v-else>
        <v-form ref="form">
          <v-flex xs12>
            <v-textarea 
              :value="override.explanation" 
              :rules="[rules.required]"
              auto-grow
              label="Explanation*" rows="3" class="mt-2"
              hint="The explanation is for internal use only and will not be shown to the provider"
              @input="update('explanation', $event)" 
            />
          </v-flex>
          <our-required-field-indicator/>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn :disabled="busy" flat @click="hide">Cancel</v-btn>
        <v-btn :disabled="busy" flat color="error" @click="save">Remove</v-btn>
      </v-card-actions>
    </v-card>
    <our-validation-dialog v-model="showServerValidationDialog" :state-error="state.error" />
    <our-validation-dialog v-model="showFormValidationDialog" :form="this.$refs.form" />
  </v-dialog>
</template>

<script>
import * as action from "@/store/levy-collection-overrides/action-types";
import * as mutation from "@/store/levy-collection-overrides/mutation-types";
import lookupMixins from "@/mixins/lookup.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import { SHOW_NOTIFICATION } from "@/store/notifications/mutation-types";

export default {
  mixins: [ruleMixins, lookupMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      validator: prop => typeof prop === "number" || prop === null,
      required: true
    },
    providerId: {
      validator: prop => typeof prop === "number" || prop === null,
      default: null
    }
  },
  data() {
    return {
      showFormValidationDialog: false,
      showServerValidationDialog: false
    };
  },
  computed: {
    title() {
      return this.selectedProviderName
        ? `Remove ${this.selectedProviderName} from levy collection`
        : "Remove provider from levy collection";
    },
    state() {
      return this.$store.state.levyCollectionOverrides.edit;
    },
    override() {
      return this.state.data;
    },
    lcpState() {
      return this.$store.state.levyCollectionProviders;
    },
    busy() {
      return this.loading || this.saving;
    },
    loading() {
      return this.state.loading || this.lcpState.view.loading;
    },
    saving() {
      return this.state.saving;
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.$store.commit(mutation.EDIT_CLEAR);
        this.$refs.form.reset();
      }
    }
  },
  methods: {
    update(field, newValue) {
      this.$store.commit(mutation.EDIT_SET_DATA, { [field]: newValue });
    },
    hide() {
      this.$emit("update:show", false);
    },
    validate() {
      if (!this.$refs.form.validate()) {
        this.showFormValidationDialog = true;
        return false;
      }
      return true;
    },
    async save() {
      if (this.validate()) {
        this.update("providerId", this.providerId);
        this.update("category", "RemoveProvider");
        const saved = await this.$store.dispatch(action.EDIT_SAVE, this.levyCollectionId);
        if (saved) {
          this.finish();
        } else {
          this.showServerValidationDialog = true;
        }
      }
    },
    finish() {
      this.hide();
      this.$router.replace(`/levy-collections/${this.levyCollectionId}/overrides`);
      this.$store.commit(SHOW_NOTIFICATION, "Provider has been removed from the levy collection.");
    }
  }
};
</script>
